import React, { useState, useRef, useEffect, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { css, StyleSheet } from 'aphrodite/no-important';
import { TextInputWithLabel, PopupMenu } from 'quantux-ui';
import { blue, salmon } from '../../theme';
import { BASE_URL } from '../../utils';

/**
 * Critial Note:
 * Parent of this component should have the position set to relative,
 * otherwise this component will not work as intended.
 * @param {*} props
 * @returns MaskedDataInput Component
 */
const MaskedDataInput = (props) => {
  // Refs
  const searchRef = useRef();
  // State variables
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  // -------------- Styles --------------
  const styles = StyleSheet.create({
    mask: {
      position: 'absolute',
      display: props.visible ? 'block' : 'none',
      top: '0',
      left: '0',
      background: 'rgba(0, 160, 255, 0.5)',
      width: '100%',
      height: '100%',
      zIndex: '1',
      padding: '2px 8px 8px',
      margin: 'auto'
    },
    container: {
      background: blue[900],
      width: '359px',
      padding: '4px 4px 16px 4px',
      boxShadow: !showSearchMenu ? '0px 2px 4px rgb(0, 0, 0)' : 'none',
      display: 'flex',
      justifyContent: 'center',
      borderRadius: '5px',
      marginTop: '8px'
    },
    inputContainer: {
      position: 'relative'
    },
    closeIconContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginRight: '-8px'
    },
    closeIcon: {
      color: 'white',
      background: salmon[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '20px',
      width: '20px'
    }
  });
  // Network requests
  const search = (value) => {
    const url = new URL(`${BASE_URL}/${props.apiSearchPath}`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowSearchMenu(true);
        setSearchResults(data[props.apiDataKey]);
      })
      .catch(error => console.log('error', error));
  };

  const debouncedSearch = useCallback(debounce(value => search(value), 800), []);
  // const debouncedSearch = useCallback(debounce(search, 800), []);

  // Effects
  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      debouncedSearch(searchTerm);
    }
  }, [searchTerm]);

  useEffect(() => {
    setSearchTerm('');
  }, [props.visible]);

  // Event handlers
  const handleOnChangeSearchTerm = (e) => {
    // e.stopPropagation();
    const { value } = e.target;
    setSearchTerm(value);
  };

  // Search results menu items
  const searchItems = [];
  // None/Others
  if (props.noneObject) {
    searchItems.push(props.menuItemTemplate({
      ...props.noneObject,
      [props.propVar]: searchTerm,
      _id: Math.random().toString(36).slice(2)
    }, '', true));
  }
  if (searchResults.length > 0) {
    searchResults.forEach(el => {
      searchItems.push(props.menuItemTemplate(el, searchTerm, false));
    });
  }

  return (
    <div className={css(styles.mask)}>
      {/* Close  icon */}
      <div className={css(styles.closeIconContainer)}>
        <div className={css(styles.closeIcon)} onClick={() => props.onClose()}>
          <i className='fa fa-close' onClick={() => props.onClose()} />
        </div>
      </div>
      <div className={css(styles.container)}>
        <div ref={searchRef} className={css(styles.inputContainer)}>
          <TextInputWithLabel
            value={searchTerm}
            showClearFieldIcon
            titleText={props.title}
            isCompactLayout={false}
            titleColor="#5079ab"
            width={343}
            showPreIcon
            preIcon="fa fa-search"
            onChange={handleOnChangeSearchTerm}
            iconSize={16}
          />
          <PopupMenu
            top={53}
            left={-8}
            zIndex='1550'
            position="absolute"
            visible={showSearchMenu}
            parent={searchRef}
            onOutsideClick={() => { setShowSearchMenu(false); }}
            className={css(styles.searchMenu)}
          >
            <div
              style={{
                width: '359px',
                background: blue[900],
                overflowY: 'auto',
                paddingBottom: '1px',
                borderRadius: '5px',
                maxHeight: '200px'
              }}
            >
              {searchItems}
            </div>
          </PopupMenu>
        </div>
      </div>
    </div>
  );
};

export default MaskedDataInput;
